import CheckGreen from "check-green.svg";
import Loader from "components/shared/loader";
import React, { useEffect, useMemo, useState } from "react";
import { applyDrivers, applyTrucks, getDriverList, getTruckList } from "sources/atiSu";
import { skipSelfRegistrationDriversAdding, skipSelfRegistrationTrucksAdding } from "sources/account";

type Driver = {
    firstName: string;
    lastName: string;
    patronymic?: string;
    phoneNumber?: string;
    isAdded: boolean;
};

type Vehicle = {
    number: string;
    isAdded: boolean;
};

type TruckList = {
    cars: Vehicle[];
    trailers: Vehicle[];
};

type AtiSuImportProps = {
    getOrg: () => unknown;
    type: "drivers" | "trucks";
};

const titles = {
    drivers: {
        action: "Добавить водителей",
        empty: "В ATI.SU нет водителей для добавления.",
        subtitle: "Добавьте водителей из ATI.SU — их данные уже подготовлены.",
        title: "Добавьте водителей из ATI.SU",
    },
    trucks: {
        action: "Добавить транспортные средства",
        empty: "В ATI.SU нет транспортных средств для добавления.",
        subtitle: "Добавьте автомобили и прицепы из ATI.SU одним действием.",
        title: "Добавьте транспортные средства из ATI.SU",
    },
};

export default function AtiSuImport({ getOrg, type }: AtiSuImportProps) {
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [trucks, setTrucks] = useState<TruckList>({ cars: [], trailers: [] });
    const [loading, setLoading] = useState(true);

    const content = titles[type];
    const vehicles = useMemo(
        () => [
            ...trucks.cars.map((truck) => ({ ...truck, type: "Автомобиль" })),
            ...trucks.trailers.map((trailer) => ({ ...trailer, type: "Прицеп" })),
        ],
        [trucks],
    );
    const items = type === "drivers" ? drivers : vehicles;
    const hasItemsToAdd = items.some((item) => !item.isAdded);

    useEffect(() => {
        let isMounted = true;
        const request = type === "drivers" ? getDriverList() : getTruckList();

        request
            .then((response) => {
                if (!isMounted || !response) {
                    return;
                }

                if (type === "drivers") {
                    setDrivers(response);
                } else {
                    setTrucks({ cars: response.cars ?? [], trailers: response.trailers ?? [] });
                }
            })
            .catch(() => undefined)
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [type]);

    const continueRegistration = (request: () => Promise<unknown>) => {
        setLoading(true);

        request()
            .then((response) => {
                if (response) {
                    return Promise.resolve(getOrg());
                }
            })
            .catch(() => undefined)
            .finally(() => setLoading(false));
    };

    const apply = () => continueRegistration(type === "drivers" ? applyDrivers : applyTrucks);
    const skip = () =>
        continueRegistration(type === "drivers" ? skipSelfRegistrationDriversAdding : skipSelfRegistrationTrucksAdding);

    return (
        <>
            {loading && (
                <div className="loader-snippet--disable-loader">
                    <Loader />
                </div>
            )}
            <div className="registration-panel__wrap registration-panel__wrap--wide">
                <div className="registration-panel__title">
                    {content.title}&nbsp;
                    <span className="font-size-52">
                        <CheckGreen />
                    </span>
                </div>
                <div className="registration-panel__subtitle">{content.subtitle}</div>
                <div className="registration-panel__container">
                    <div className="registration-panel">
                        <div className="d-flex flex-column w-100">
                            <div className="registration-panel__inner--title">Данные из ATI.SU</div>
                            <div className="registration-panel__inner ati-su-import">
                                {items.length === 0 ? (
                                    <div className="ati-su-import__empty">{content.empty}</div>
                                ) : (
                                    <div className="container-fluid table-responsive p-0 ati-su-import__table">
                                        <table className="table table-sm table-bordered m-0">
                                            <thead className="bg-white sticky-header">
                                                <tr>
                                                    <th>#</th>
                                                    {type === "drivers" ? (
                                                        <>
                                                            <th>ФИО</th>
                                                            <th>Телефон</th>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <th>Тип</th>
                                                            <th>Номер</th>
                                                        </>
                                                    )}
                                                    <th>Статус</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {type === "drivers"
                                                    ? drivers.map((driver, index) => (
                                                          <tr key={`${driver.phoneNumber}-${index}`}>
                                                              <td>{index + 1}</td>
                                                              <td>
                                                                  {[
                                                                      driver.lastName,
                                                                      driver.firstName,
                                                                      driver.patronymic,
                                                                  ]
                                                                      .filter(Boolean)
                                                                      .join(" ")}
                                                              </td>
                                                              <td>{driver.phoneNumber || "—"}</td>
                                                              <td>
                                                                  <ImportStatus isAdded={driver.isAdded} />
                                                              </td>
                                                          </tr>
                                                      ))
                                                    : vehicles.map((vehicle, index) => (
                                                          <tr key={`${vehicle.type}-${vehicle.number}-${index}`}>
                                                              <td>{index + 1}</td>
                                                              <td>{vehicle.type}</td>
                                                              <td>{vehicle.number}</td>
                                                              <td>
                                                                  <ImportStatus isAdded={vehicle.isAdded} />
                                                              </td>
                                                          </tr>
                                                      ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 mb-2 text-center registration-panel__inner">
                                <button className="r-form__button" type="button" onClick={apply} disabled={loading}>
                                    {hasItemsToAdd ? content.action : "Продолжить"}
                                </button>
                                <button
                                    className="r-relay__help-link ati-su-import__skip-button"
                                    type="button"
                                    onClick={skip}
                                    disabled={loading}
                                >
                                    Пропустить этот шаг
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function ImportStatus({ isAdded }: { isAdded: boolean }) {
    if (isAdded) {
        return <span className="ati-su-import__status">Уже добавлен</span>;
    }

    return (
        <span className="ati-su-import__status ati-su-import__status--pending">
            <CheckGreen /> Будет добавлен
        </span>
    );
}
